import React, { useEffect, useState, useId } from 'react';
import { TCN, TCN_COLOR_MAP, autoRingColor } from './tokens';

/**
 * TCN RingChart — animated SVG ring (aligned on mockup ui-kit.jsx:218-254).
 *
 * Props:
 *  - value: number (current)
 *  - target: number (denominator) — pass 100 to use `value` as a percentage directly
 *  - size: outer width/height in px (default 140 per mockup)
 *  - stroke: ring stroke width in px (default 12 per mockup)
 *  - color: explicit color OR semantic key — defaults to autoRingColor(value,target)
 *           (NB: ring auto-color returns 'cyan' sub-80%, not 'red')
 *  - label: big number rendered in center (default = rounded percentage)
 *  - sub: small label under the big number
 *  - cap: 'round' | 'butt' | 'square' (default 'round')
 */
const RingChart = ({
  value = 0,
  target = 100,
  size = 140,
  stroke = 12,
  color,
  label,
  sub,
  cap = 'round',
  className = '',
  style,
}) => {
  const id = useId();
  const ratio = target > 0 ? Math.max(0, Math.min(value / target, 1.5)) : 0;
  const displayedRatio = Math.min(ratio, 1);
  const semantic = color ?? autoRingColor(value, target);
  const strokeColor = TCN_COLOR_MAP[semantic] ?? semantic;

  const [animatedRatio, setAnimatedRatio] = useState(0);
  useEffect(() => {
    // Trigger transition on mount (frame skip ensures the dasharray transition fires)
    const raf = requestAnimationFrame(() => setAnimatedRatio(displayedRatio));
    return () => cancelAnimationFrame(raf);
  }, [displayedRatio]);

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dashOffset = c * (1 - animatedRatio);

  const percentLabel = label ?? `${Math.round(ratio * 100)}%`;

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size, ...style }}
    >
      <svg width={size} height={size} role="img" aria-label={`Ring ${Math.round(ratio * 100)}%`}>
        {/* Track — mockup ui-kit.jsx:233 = rgba(255,255,255,0.08) (alpha, not solid) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={stroke}
        />
        {/* Progress — no drop-shadow per mockup */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth={stroke}
          strokeLinecap={cap}
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: `stroke-dashoffset ${TCN.durChart} ${TCN.ease}`,
          }}
          key={id}
        />
      </svg>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center text-center"
        style={{ fontFamily: TCN.fontSans, color: TCN.text }}
      >
        <div
          style={{
            fontSize: Math.round(size / 4.2),
            fontWeight: 700,
            letterSpacing: '-0.8px',
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}
        >
          {percentLabel}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: TCN.textDim,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              marginTop: 4,
            }}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
};

export default RingChart;
